'use client';

import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import {
  ActionMenu,
  Button,
  Link,
  Separator,
  toast,
  type ActionMenuItem,
} from '@mokoboko/components';
import {
  Layers,
  RefreshCw,
  Calendar,
  CalendarPlus,
  ChevronDown,
  Coins,
  Gift,
  UserRound,
  Mail,
  MonitorDown,
  Smartphone,
} from 'lucide-react';
import { UserIcon } from '@/icons';
import { User, USER_QUERY_KEY } from '@/state/user';
import { logout } from '@/services/auth';
import useTopup from '@/state/topup-content';
import useTopupEligibility from '@/state/topup';
import useOfferingAvailability, {
  useClassAvailability,
  useGiftCardAvailability,
} from '@/hooks/use-offering-availability';
import useSubscriptionCodes from '@/state/subscription-codes';
import useClassPackCodes from '@/state/class-pack-code';
import { useInstallPlatform } from '@/hooks/use-install-platform';
import { openInstallHelp } from '@/lib/home-screen-app-store';
import {
  installActionLabel,
  isPhonePlatform,
  type OfferPlatform,
} from '@/lib/install-platform';

const buildBuyItems = (
  router: ReturnType<typeof useRouter>,
  {
    hasClassPacks,
    hasSubscriptions,
    hasClasses,
    topupSlug,
    hasGiftCards,
  }: {
    hasClassPacks: boolean;
    hasSubscriptions: boolean;
    hasClasses: boolean;
    topupSlug: string | null;
    hasGiftCards: boolean;
  }
): ActionMenuItem[] => [
  ...(hasClassPacks
    ? [
        {
          key: 'class-packs',
          label: 'Class packs',
          icon: Layers,
          onClick: () => router.push('/packages'),
        },
      ]
    : []),
  ...(hasSubscriptions
    ? [
        {
          key: 'subscriptions',
          label: 'Memberships',
          icon: RefreshCw,
          onClick: () => router.push('/subscriptions'),
        },
      ]
    : []),
  ...(hasClasses
    ? [
        {
          key: 'book-class',
          label: 'Buy single class',
          icon: CalendarPlus,
          onClick: () => router.push('/classes'),
        },
      ]
    : []),
  ...(topupSlug
    ? [
        {
          key: 'top-up',
          label: 'Extra credits',
          icon: Coins,
          onClick: () => router.push(`/${topupSlug}`),
        },
      ]
    : []),
  ...(hasGiftCards
    ? [
        {
          key: 'gift-cards',
          label: 'Gift cards',
          icon: Gift,
          onClick: () => router.push('/gift-cards'),
        },
      ]
    : []),
];

const MENU_CHEVRON = (
  <ChevronDown
    size={16}
    className="shrink-0 transition-transform duration-200 group-aria-expanded:rotate-180"
  />
);

const getCreditsItem = (hasSubs: boolean, hasPacks: boolean) => {
  if (hasSubs && hasPacks) {
    return { label: 'My membership & pack', icon: Layers };
  }
  if (hasSubs) return { label: 'My membership', icon: RefreshCw };
  return { label: 'My pack', icon: Layers };
};

const buildAccountItems = (
  router: ReturnType<typeof useRouter>,
  {
    hasSubs,
    hasPacks,
    installPlatform,
  }: {
    hasSubs: boolean;
    hasPacks: boolean;
    installPlatform: OfferPlatform | null;
  }
): ActionMenuItem[] => [
  {
    key: 'account-settings',
    label: 'Account settings',
    icon: UserRound,
    onClick: () => router.push('/dashboard/account'),
  },
  ...(hasSubs || hasPacks
    ? [
        {
          key: 'credits',
          ...getCreditsItem(hasSubs, hasPacks),
          onClick: () => router.push('/dashboard/credits'),
        },
      ]
    : []),
  {
    key: 'about-studio',
    label: 'About studio',
    icon: Mail,
    onClick: () => router.push('/dashboard/about'),
  },
  ...(installPlatform
    ? [
        {
          key: 'install-app',
          label: installActionLabel(installPlatform),
          icon: isPhonePlatform(installPlatform) ? Smartphone : MonitorDown,
          onClick: () => openInstallHelp('account_menu'),
        },
      ]
    : []),
];

export const UserProfileCard = ({ user }: { user: User }) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const { data: topup } = useTopup();
  const { data: eligibility } = useTopupEligibility();

  const {
    hasSubscriptions,
    hasClassPacks,
    isLoading: isCatalogueLoading,
  } = useOfferingAvailability();
  const {
    classes,
    hasClasses,
    isLoading: isClassesLoading,
  } = useClassAvailability();
  const bookHref =
    classes.length === 1 ? `/classes/${classes[0].slug}` : '/classes';
  const { hasGiftCards, isLoading: isGiftCardsLoading } =
    useGiftCardAvailability();
  const isActionsLoading =
    isCatalogueLoading || isClassesLoading || isGiftCardsLoading;

  const { data: subscriptionCodes, hasFailedRead: subsFailedRead } =
    useSubscriptionCodes({
      email: user.email,
    });
  const { data: classPackCodes, hasFailedRead: packsFailedRead } =
    useClassPackCodes({ email: user.email });
  // A failed read is not "you own none": hiding the shortcut takes away the
  // route to the page that would have said the read failed. Offering it is the
  // recoverable answer, since that page reports the failure itself.
  const hasSubs = subsFailedRead || (subscriptionCodes?.length ?? 0) > 0;
  const hasPacks = packsFailedRead || (classPackCodes?.length ?? 0) > 0;
  const installPlatform = useInstallPlatform();
  const accountItems = useMemo(
    () => buildAccountItems(router, { hasSubs, hasPacks, installPlatform }),
    [router, hasSubs, hasPacks, installPlatform]
  );

  const topupSlug = topup && eligibility?.eligible ? topup.slug : null;
  const buyItems = useMemo(
    () =>
      buildBuyItems(router, {
        hasClassPacks,
        hasSubscriptions,
        hasClasses,
        topupSlug,
        hasGiftCards,
      }),
    [
      router,
      hasClassPacks,
      hasSubscriptions,
      hasClasses,
      topupSlug,
      hasGiftCards,
    ]
  );

  const onLogout = async () => {
    try {
      setIsProcessing(true);
      await logout();
      queryClient.setQueryData(USER_QUERY_KEY, null);
      router.replace('/');
    } catch (error) {
      toast.error('Unable to log out, please try again later!');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex flex-row items-baseline justify-between gap-2 sm:flex-col sm:items-start sm:gap-1.5">
            <h1 className="heading-page text-xl">
              Hey,{' '}
              {user?.first_name
                ? user.first_name.charAt(0).toUpperCase() +
                  user.first_name.slice(1)
                : ''}
            </h1>
            <Link
              color="red"
              size="md"
              isStaticIcon
              onClick={onLogout}
              role="button"
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onLogout();
                }
              }}
              className="shrink-0 cursor-pointer whitespace-nowrap sm:hidden"
            >
              Log out
            </Link>
            <p className="hidden text-sm text-gray-700 sm:block">
              Manage profile, book class or buy a service
            </p>
          </div>
          <p className="text-sm text-gray-700 sm:hidden">
            Manage profile, book class or buy a service
          </p>
          <Link
            color="red"
            size="md"
            isStaticIcon
            onClick={onLogout}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onLogout();
              }
            }}
            className="hidden cursor-pointer self-start sm:flex"
          >
            Log out
          </Link>
        </div>

        <Separator />

        <div className="flex w-full flex-wrap items-center gap-4 sm:w-auto">
          <div className="flex flex-1 sm:flex-initial [&>div]:w-full max-sm:[&_.absolute]:!w-full max-sm:[&_.absolute]:!max-w-none">
            <ActionMenu
              label="Account"
              panelMinWidth={250}
              trigger={
                <Button
                  variant="secondary-gray"
                  leadingIcon={<UserIcon />}
                  trailingIcon={MENU_CHEVRON}
                  size="lg"
                  className="group w-full justify-center whitespace-nowrap"
                >
                  Account
                </Button>
              }
              items={accountItems}
            />
          </div>

          {/* Gated together so the row settles once, not once per query. */}
          {!isActionsLoading && (
            <>
              {buyItems.length > 0 && (
                <div className="flex flex-1 sm:flex-initial [&>div]:w-full [&_.absolute]:left-auto [&_.absolute]:right-0 sm:[&_.absolute]:left-0 sm:[&_.absolute]:right-auto">
                  <ActionMenu
                    label="Buy"
                    panelMinWidth={200}
                    trigger={
                      <Button
                        variant="secondary-gray"
                        leadingIcon={<Layers size={16} />}
                        trailingIcon={MENU_CHEVRON}
                        size="lg"
                        className="group w-full justify-center"
                      >
                        Buy
                      </Button>
                    }
                    items={buyItems}
                  />
                </div>
              )}

              {hasClasses && (
                <Button
                  variant="primary"
                  leadingIcon={<Calendar size={20} />}
                  onClick={() => router.push(bookHref)}
                  size="lg"
                  className="basis-full justify-center sm:basis-auto"
                >
                  Book class
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
